import React, { ReactNode } from 'react'
import Settings from './Settings'
import { isEmpty, noop, uniqueId } from 'lodash'
import { SvgEdit, SvgView } from '../assets/images'
import { ColumnListType, DataType, OptionsListType } from '../utils'
import DraggableMultiSelect from './DraggableMultiselect'
import ToggleButton from './ToggleButton'
import Checkbox from './Checkbox'
import Tooltip from './Tooltip'

interface TablePropType {
  columnList: ColumnListType[]
  data: DataType[]
  additionalColumns: ColumnListType[]
  optionsList?: OptionsListType[]
  columnSetter?: React.Dispatch<React.SetStateAction<ColumnListType[]>>
  handleChange: (
    e:
      | React.MouseEvent<HTMLDivElement, MouseEvent>
      | React.ChangeEvent<HTMLInputElement>,
    selectionType: string,
    index?: number,
    record?: DataType
  ) => void
  handleRowClick?: (
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    record: DataType,
    index: number
  ) => void
  headerClick: (
    e: React.MouseEvent<HTMLTableHeaderCellElement, MouseEvent>,
    key: string
  ) => void
  edit?: boolean
  view?: boolean
  children?: (data: DataType) => ReactNode
  message?: string
  rowBreak?: boolean
  toggle?: boolean
}

const Actions: React.FC<{
  optionsList: OptionsListType[]
  data: DataType
  handleChange: (
    e:
      | React.MouseEvent<HTMLDivElement, MouseEvent>
      | React.ChangeEvent<HTMLInputElement>,
    selectionType: string,
    index?: number,
    record?: DataType
  ) => void
  edit?: boolean
  view?: boolean
  index: number
  toggle?: boolean
}> = ({ optionsList, data, handleChange, edit, view, index, toggle }) => {
  const handleClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    action: string
  ) => {
    e.stopPropagation()
    e.nativeEvent.stopImmediatePropagation()
    handleChange(e, action, index, data)
  }

  const onToggleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation()
  }

  return (
    <div className="flex items-center gap-8 justify-start px-2">
      {view && (
        <div onClick={(e) => handleClick(e, 'view')} data-action="view">
          <Tooltip content="View">
            <SvgView
              width={'1.25rem'}
              height={'1.25rem'}
              className="text-gray-500 hover:text-blue-700"
            />
          </Tooltip>
        </div>
      )}
      {edit && (
        <div
          onClick={
            data.enabled === undefined ||
            data.enabled === null ||
            data.enabled === true
              ? (e) => handleClick(e, 'edit')
              : undefined
          }
          data-action="edit"
        >
          <Tooltip content="Edit">
            <SvgEdit
              width={'1.25rem'}
              height={'1.25rem'}
              className={`${
                data.enabled === undefined ||
                data.enabled === null ||
                data.enabled === true
                  ? 'cursor-pointer'
                  : 'cursor-not-allowed'
              } hover:fill-primary-500`}
            />
          </Tooltip>
        </div>
      )}
      {data.enabled !== undefined && toggle && (
        <div data-action="toggle">
          <Tooltip content="Toggle">
            <div className="mb-1" onClick={onToggleClick}>
              <ToggleButton
                name=""
                register={undefined}
                validation={undefined}
                errors={undefined}
                onClick={(e) => handleClick(e, 'toggle')}
                checked={!!data.enabled}
              />
            </div>
          </Tooltip>
        </div>
      )}
      {optionsList.length > 0 && (
        <Tooltip content="Settings">
          <div
            className={`${
              data.enabled === undefined ||
              data.enabled === null ||
              data.enabled === true
                ? 'cursor-pointer'
                : 'cursor-not-allowed opacity-50'
            }`}
          >
            <Settings
              settingsList={
                data.enabled === undefined ||
                data.enabled === null ||
                data.enabled === true
                  ? optionsList
                  : []
              }
              rowIndex={index}
              record={data}
              handleChange={handleChange}
              position="bottom"
            />
          </div>
        </Tooltip>
      )}
    </div>
  )
}

const Table: React.FC<TablePropType> = ({
  columnList,
  data,
  optionsList,
  additionalColumns,
  columnSetter,
  handleChange,
  handleRowClick,
  headerClick,
  edit,
  view,
  children,
  message,
  rowBreak = false,
  toggle,
}) => {
  let displayValue: string, tooltipContent: string
  const selectedData = data?.filter(
    (dataItem) => dataItem.selected === 'true' || dataItem.selected === true
  )
  return (
    <div className="overflow-x-auto min-h-[25rem] bg-white">
      <table className="w-full border-collapse">
        <thead className="bg-accent-50 sticky top-0 z-10">
          <tr>
            {columnList?.map((column) => (
              <th
                key={uniqueId()}
                onClick={(e) => headerClick(e, column.key)}
                className={`p-2 text-left text-base text-ellipsis`}
              >
                <div className="flex gap-2 items-center">
                  {column?.type?.toLowerCase() !== 'multiselect' &&
                    (column?.label?.toLowerCase() !== 'select' ||
                      column?.type?.toLowerCase() === 'checkbox') && (
                      <span className="font-normal text-xs">
                        {column?.label}
                      </span>
                    )}
                  {column?.Icon}
                </div>
                {column?.type?.toLowerCase() === 'multiselect' && (
                  <Tooltip content="Add column" position="left">
                    <DraggableMultiSelect
                      colList={additionalColumns}
                      currentCol={columnList}
                      colSetter={columnSetter || noop}
                      data-action="multiselect"
                    />
                  </Tooltip>
                )}
                {column?.type?.toLowerCase() === 'select' ||
                  (column?.type?.toLowerCase() === 'checkbox' && (
                    <Checkbox
                      name=""
                      label=""
                      onClick={(e) => handleChange(e, 'all', -1)}
                      checked={
                        data.length > 0 && selectedData?.length === data?.length
                      }
                    />
                  ))}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data?.map((dataItem, index) => (
            <tr
              className={`${
                dataItem.enabled === undefined ||
                dataItem.enabled === null ||
                dataItem.enabled === true
                  ? 'hover:bg-secondary-25'
                  : 'hover:bg-[#F7F7F7]'
              } ${handleRowClick ? 'cursor-pointer' : ''}`}
              key={uniqueId()}
              onClick={
                dataItem.enabled === undefined ||
                dataItem.enabled === null ||
                dataItem.enabled === true
                  ? (e) => {
                      handleRowClick && handleRowClick(e, dataItem, index)
                    }
                  : noop
              }
            >
              {columnList.map((column) => {
                const fieldKey = column.key
                const fieldValue = dataItem[fieldKey]

                return (
                  <td
                    className="p-2 leading-5"
                    key={uniqueId()}
                    data-action={`${index}`}
                  >
                    {(() => {
                      switch (column.type) {
                        case 'checkbox':
                          return (
                            <div data-action="checkbox">
                              <Checkbox
                                label=""
                                name=""
                                checked={!!dataItem.selected}
                                onChange={(e) =>
                                  handleChange(e, 'checkbox', index, dataItem)
                                }
                              />
                            </div>
                          )
                        case 'radio':
                          return (
                            <input
                              type="radio"
                              checked={!!dataItem.selected}
                              onClick={(e) =>
                                handleChange(e, 'radio', index, dataItem)
                              }
                            />
                          )
                        case 'image':
                          return (
                            <img
                              src={String(fieldValue)}
                              alt={String(fieldValue)}
                              className="h-10 w-12"
                            />
                          )
                        case 'actions':
                          return (
                            <Actions
                              optionsList={optionsList || []}
                              data={dataItem}
                              handleChange={handleChange}
                              edit={edit ?? false}
                              view={view ?? false}
                              toggle={toggle ?? false}
                              index={index}
                            />
                          )
                        case 'multiselect':
                          return null
                        case 'component':
                          return children ? children(dataItem) : null

                        case 'customcheckbox': {
                          return (
                            <Checkbox
                              label=""
                              name=""
                              checked={!!dataItem?.[fieldKey]}
                              onChange={(e) =>
                                handleChange(e, fieldKey, index, dataItem)
                              }
                              key={`${fieldKey ?? ''}-${index}`}
                            />
                          )
                        }
                        default:
                          displayValue =
                            fieldValue !== undefined && fieldValue !== null
                              ? Array.isArray(fieldValue)
                                ? fieldValue.length > 2
                                  ? fieldValue
                                      .slice(0, 2)
                                      .map(
                                        (element) =>
                                          String(element)
                                            .charAt(0)
                                            .toUpperCase() +
                                          String(element).slice(1)
                                      )
                                      .join(', ') + ' ...'
                                  : String(fieldValue)
                                : typeof fieldValue === 'boolean'
                                ? fieldValue
                                  ? 'true'
                                  : 'false'
                                : String(fieldValue).split(', ').length > 2
                                ? String(fieldValue)
                                    .split(', ')
                                    .slice(0, 2)
                                    .map(
                                      (element) =>
                                        String(element)
                                          .charAt(0)
                                          .toUpperCase() +
                                        String(element).slice(1)
                                    )
                                    .join(', ') + ' ...'
                                : String(fieldValue)
                              : ''
                          tooltipContent = Array.isArray(fieldValue)
                            ? fieldValue
                                .map(
                                  (element) =>
                                    String(element).charAt(0).toUpperCase() +
                                    String(element).slice(1)
                                )
                                .join(', ')
                            : isEmpty(displayValue)
                            ? '--'
                            : displayValue

                          return (
                            <Tooltip
                              content={
                                !rowBreak
                                  ? tooltipContent
                                  : tooltipContent.split('$').join(',')
                              }
                              position="top"
                              className={
                                displayValue.length < 20 ? 'w-fit' : 'w-40'
                              }
                            >
                              <span
                                className={`font-light font-primary text-xs ${
                                  dataItem.enabled === false
                                    ? 'text-black opacity-50 cursor-not-allowed'
                                    : 'text-base'
                                }`}
                              >
                                {isEmpty(displayValue)
                                  ? '--'
                                  : !rowBreak
                                  ? displayValue
                                  : displayValue
                                      .split('$')
                                      .map((item) => item.trim())
                                      .map((item, index) => (
                                        <span key={`${item}-${index}`}>
                                          {item}
                                          <br />
                                        </span>
                                      ))}
                              </span>
                            </Tooltip>
                          )
                      }
                    })()}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && (
        <div className="h-[20rem] flex items-center justify-center w-full">
          <span className="font-medium">{message}</span>
        </div>
      )}
    </div>
  )
}

export default Table
